import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { addWindow, resetWindows, setMobileView } from 'store/slice/pdpStateSlice';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { LOCALES } from 'localization/constants/configEnums';
import { OffersProvider } from 'productTemplate/providers/OffersProvider';
import { SizeConversionProvider } from 'productTemplate/providers/SizeConversionProvider';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { useFetchProductVariantQuery } from 'store/api/productVariantApi';
import { useLocation } from 'shared/hooks/useLocation';
import { useStoreDispatch } from 'store/hooks/storeHooks';

const ProductDetailPageDesktop = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageDesktop').then(
      (mod) => mod.ProductDetailPageDesktop,
    ),
  { ssr: true },
);

interface IProductDetailPage {
  isCrawler: boolean;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
}

const ProductDetailPageLanding = ({ productTemplate }: IProductDetailPage) => {
  const { query, asPath, events } = useRouter();
  const storeDispatch = useStoreDispatch();
  const { selectedCountryCode } = useLocation();
  const { id, isAvailabilityRestricted } = productTemplate || {};

  const [variantIdFromUrl, setVariantIdFromUrl] = useState<string>(null);
  const { data: productVariantData } = useFetchProductVariantQuery(
    { id: variantIdFromUrl, countryCode: selectedCountryCode },
    { skip: !variantIdFromUrl },
  );
  const isItemAvailabilityRestricted = isAvailabilityRestricted;
  const { isFetching } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isItemAvailabilityRestricted },
  );

  useEffect(() => {
    const hash = /#id=((\w|-)+)/.exec(asPath);
    if (query && query.productId) {
      setVariantIdFromUrl(query.productId as string);
    } else if (hash && hash[1]) {
      setVariantIdFromUrl(hash[1]);
    }
  }, [query, asPath]);

  // Show variant window with a delay to display on top of facts window.
  // 60ms delay because the facts window shows up with a 50ms delay.
  useEffect(() => {
    let timer;
    if (productVariantData) {
      timer = setTimeout(() => {
        storeDispatch(addWindow(productVariantData));
        storeDispatch(setMobileView({ view: 'detail', size: productVariantData.size }));
      }, 60);
    }
    return () => clearTimeout(timer);
  }, [productVariantData]);

  const handleBeforeUnload = () => {
    storeDispatch(resetWindows());
  };

  useEffect(() => {
    events.on('routeChangeStart', handleBeforeUnload);

    return () => {
      events.off('routeChangeStart', handleBeforeUnload);
    };
  }, []);

  return (
    <SizeConversionProvider>
      <OffersProvider>
        <ProductDetailPageDesktop
          isAvailabilityRestricted={isItemAvailabilityRestricted}
          isFetchingBuyBarData={isFetching}
        />
      </OffersProvider>
    </SizeConversionProvider>
  );
};

export default ProductDetailPageLanding;
